import GooglePayConfig from '@/configuration/sources/smartform/googlePay.yml'
import { CoBrands, SubBrands } from '@/configuration/sources/Brands.yml'

const { brandMapping, allowedBrands, allowedAuthMethods } = GooglePayConfig

const baseRequest = {
  apiVersion: 2,
  apiVersionMinor: 0,
  emailRequired: true
}

function formatBrand(brand) {
  // LATAM brand variation to generic brand
  let formattedBrand = CoBrands.brands[brand] ?? brand
  // Specific changes of sub-brands must adapt to googlePay supported brands
  formattedBrand = SubBrands[formattedBrand] ?? formattedBrand
  return formattedBrand
}

function isAllowedGoogleBrand(brand, cbEnabled, disableVisaOnWallet) {
  return (
    (allowedBrands.includes(brand) ||
      (cbEnabled && brand === 'CARTES_BANCAIRES')) &&
    !(disableVisaOnWallet && brand === 'VISA')
  )
}

function getAllowedGoogleCardNetworks({
  brands,
  cbEnabled,
  disableVisaOnWallet
}) {
  let allowedGoogleBrands = brands
    .map(formatBrand)
    .map(brand => brandMapping[brand])
    .filter(
      brand =>
        !!brand && isAllowedGoogleBrand(brand, cbEnabled, disableVisaOnWallet)
    )

  return Array.from(new Set(allowedGoogleBrands))
}

export function getIsReadyToPayRequest({
  cvcRequired,
  dnaBrands,
  dnaMetadataCards,
  shippingAddressRequired,
  disableVisaOnWallet,
  cbEnabled
}) {
  return {
    ...baseRequest,
    allowedPaymentMethods: [
      baseCardPaymentMethod({
        cvcRequired,
        dnaBrands,
        dnaMetadataCards,
        shippingAddressRequired,
        disableVisaOnWallet,
        cbEnabled
      })
    ]
  }
}

/**
 * @typedef {{merchantName: string, merchantId: string, gateway: string, gatewayMerchantId: string, cvcRequired: boolean | undefined , countryCode: string, currencyCode: string, totalPrice: string, dnaBrands: string[], dnaMetadataCards: string[], cbEnabled?: boolean, brandPriority?: string[] }} GooglePaymentParams
 */
/**
 * Configure support for the Google Pay API
 *  @param {GooglePaymentParams} param0
 * @see {@link https://developers.google.com/pay/api/web/reference/object#PaymentDataRequest|PaymentDataRequest}
 * @returns {object} PaymentDataRequest fields
 */
export function getGooglePaymentDataRequest({
  merchantName,
  merchantId,
  merchantOrigin,
  gateway,
  gatewayMerchantId,
  cvcRequired,
  countryCode,
  currencyCode,
  totalPrice,
  dnaBrands,
  dnaMetadataCards,
  shippingAddressRequired,
  disableVisaOnWallet,
  cbEnabled,
  brandPriority
}) {
  return {
    ...baseRequest,
    allowedPaymentMethods: [
      getCardPaymentMethod({
        gateway,
        gatewayMerchantId,
        cvcRequired,
        dnaBrands,
        dnaMetadataCards,
        shippingAddressRequired,
        disableVisaOnWallet,
        cbEnabled,
        brandPriority
      })
    ],
    transactionInfo: getGoogleTransactionInfo({
      countryCode,
      currencyCode,
      totalPrice
    }),
    merchantInfo: { merchantId, merchantName, merchantOrigin },
    shippingAddressRequired
  }
}

export function getAllowedCardNetworks({
  dnaBrands,
  dnaMetadataCards,
  cbEnabled,
  disableVisaOnWallet
}) {
  // If DNA has specific card brands for Google Pay, use those
  if (dnaMetadataCards?.length) {
    return getAllowedGoogleCardNetworks({
      brands: dnaMetadataCards,
      cbEnabled,
      disableVisaOnWallet
    })
  }

  if (!dnaBrands) return []

  return getAllowedGoogleCardNetworks({
    brands: dnaBrands,
    cbEnabled,
    disableVisaOnWallet
  })
}

export function getMerchantPreferredCobadgedCardNetworks({
  allowedCardNetworks,
  brandPriority,
  cbEnabled
}) {
  if (!cbEnabled || !allowedCardNetworks?.length) return []

  const priorityOrder = new Map(
    (brandPriority || []).reduce((accumulator, brand, index) => {
      const mappedBrand = brandMapping[formatBrand(brand)]

      if (mappedBrand && !accumulator.has(mappedBrand))
        accumulator.set(mappedBrand, index)

      return accumulator
    }, new Map())
  )

  return [...new Set(allowedCardNetworks)].sort((brandA, brandB) => {
    const indexA = priorityOrder.has(brandA)
      ? priorityOrder.get(brandA)
      : Number.MAX_SAFE_INTEGER
    const indexB = priorityOrder.has(brandB)
      ? priorityOrder.get(brandB)
      : Number.MAX_SAFE_INTEGER

    if (indexA !== indexB) return indexA - indexB

    return (
      allowedCardNetworks.indexOf(brandA) - allowedCardNetworks.indexOf(brandB)
    )
  })
}

function baseCardPaymentMethod({
  cvcRequired,
  dnaBrands,
  dnaMetadataCards,
  shippingAddressRequired,
  disableVisaOnWallet,
  cbEnabled,
  brandPriority
}) {
  const allowedCardNetworks = getAllowedCardNetworks({
    dnaBrands,
    dnaMetadataCards,
    cbEnabled,
    disableVisaOnWallet
  })

  const merchantPreferredCobadgedCardNetworks =
    getMerchantPreferredCobadgedCardNetworks({
      allowedCardNetworks,
      brandPriority,
      cbEnabled
    })

  return {
    type: 'CARD',
    parameters: {
      allowedAuthMethods,
      allowedCardNetworks,
      billingAddressRequired: true,
      billingAddressParameters: {
        // When asking for the shipping address we need to ask for the complete Billing details as well
        format: shippingAddressRequired ? 'FULL' : 'MIN'
      },
      assuranceDetailsRequired: true,
      cvcRequired,
      ...(merchantPreferredCobadgedCardNetworks.length && {
        merchantPreferredCobadgedCardNetworks
      })
    }
  }
}

/**
 * Identify your gateway and your site's gateway merchant identifier
 *
 * The Google Pay API response will return an encrypted payment method capable
 * of being charged by a supported gateway after payer authorization
 *
 * @todo check with your gateway on the parameters to pass
 * @see {@link https://developers.google.com/pay/api/web/reference/object#Gateway|PaymentMethodTokenizationSpecification}
 */
function tokenizationSpecification({ gateway, gatewayMerchantId }) {
  return {
    type: 'PAYMENT_GATEWAY',
    parameters: {
      gateway,
      gatewayMerchantId
    }
  }
}

/**
 * Describe your site's support for the CARD payment method including optional
 * fields
 *
 * @see {@link https://developers.google.com/pay/api/web/reference/object#CardParameters|CardParameters}
 */
function getCardPaymentMethod({
  gateway,
  gatewayMerchantId,
  cvcRequired,
  dnaBrands,
  dnaMetadataCards,
  shippingAddressRequired,
  disableVisaOnWallet,
  cbEnabled,
  brandPriority
}) {
  return {
    ...baseCardPaymentMethod({
      cvcRequired,
      dnaBrands,
      dnaMetadataCards,
      shippingAddressRequired,
      disableVisaOnWallet,
      cbEnabled,
      brandPriority
    }),
    tokenizationSpecification: tokenizationSpecification({
      gateway,
      gatewayMerchantId
    })
  }
}

/**
 * Provide Google Pay API with a payment amount, currency, and amount status
 *
 * @see {@link https://developers.google.com/pay/api/web/reference/object#TransactionInfo|TransactionInfo}
 * @returns {object} transaction info, suitable for use as transactionInfo property of PaymentDataRequest
 */
function getGoogleTransactionInfo({ countryCode, totalPrice, currencyCode }) {
  return {
    currencyCode,
    totalPriceStatus: 'ESTIMATED',
    // set to cart total
    totalPrice,
    countryCode: countryCode
  }
}
